import * as fabric from "fabric"

/**
 * Export the mask from the canvas as a PNG blob.
 * Transparent pixels = edit here, opaque white pixels = keep (Azure convention).
 */
export function exportMask(
  fabricCanvas: fabric.Canvas,
  originalWidth: number,
  originalHeight: number
): Blob | null {
  // Create a temporary canvas
  const tempCanvas = document.createElement("canvas")
  tempCanvas.width = originalWidth
  tempCanvas.height = originalHeight
  const ctx = tempCanvas.getContext("2d")
  if (!ctx) return null

  // Fill with opaque white (= "keep" for Azure)
  ctx.fillStyle = "white"
  ctx.fillRect(0, 0, originalWidth, originalHeight)

  // Get all mask objects (not the background image)
  const objects = fabricCanvas.getObjects().filter((obj) => !(obj instanceof fabric.FabricImage))

  if (objects.length === 0) return null

  // Render mask objects as transparent areas on the temp canvas
  // We need to draw the mask objects then punch out transparency
  const maskCanvas = document.createElement("canvas")
  maskCanvas.width = originalWidth
  maskCanvas.height = originalHeight
  const maskCtx = maskCanvas.getContext("2d")
  if (!maskCtx) return null

  // Get the scale factor of the background image to map coordinates
  const bgImage = fabricCanvas.getObjects().find((obj) => obj instanceof fabric.FabricImage) as
    | fabric.FabricImage
    | undefined
  if (!bgImage) return null

  const scaleX = originalWidth / (bgImage.width! * bgImage.scaleX!)
  const scaleY = originalHeight / (bgImage.height! * bgImage.scaleY!)
  const offsetX = bgImage.left! * scaleX
  const offsetY = bgImage.top! * scaleY

  // Draw mask objects in white on black background
  maskCtx.fillStyle = "black"
  maskCtx.fillRect(0, 0, originalWidth, originalHeight)

  // Render each mask object
  for (const obj of objects) {
    maskCtx.save()
    maskCtx.translate(-offsetX, -offsetY)
    maskCtx.scale(scaleX, scaleY)

    if (obj instanceof fabric.Path) {
      const pathData = obj.path
      // Convert path command array to SVG path data string
      const svgPathStr = pathData.map((cmd: (string | number)[]) => cmd.join(" ")).join(" ")
      const path2d = new Path2D(svgPathStr)

      // Position the path: translate to object center, then undo pathOffset
      const cx = obj.left! + (obj.width! * obj.scaleX) / 2
      const cy = obj.top! + (obj.height! * obj.scaleY) / 2
      maskCtx.translate(cx, cy)
      maskCtx.translate(-obj.pathOffset.x, -obj.pathOffset.y)

      // Stroke the path with correct brush width (not fill)
      maskCtx.strokeStyle = "white"
      maskCtx.lineWidth = obj.strokeWidth
      maskCtx.lineCap = "round"
      maskCtx.lineJoin = "round"
      maskCtx.stroke(path2d)
    } else if (obj instanceof fabric.Rect) {
      maskCtx.fillStyle = "white"
      maskCtx.fillRect(obj.left!, obj.top!, obj.width! * obj.scaleX, obj.height! * obj.scaleY)
    } else if (obj instanceof fabric.Ellipse) {
      maskCtx.fillStyle = "white"
      maskCtx.beginPath()
      maskCtx.ellipse(
        obj.left! + obj.rx!,
        obj.top! + obj.ry!,
        obj.rx! * obj.scaleX,
        obj.ry! * obj.scaleY,
        0,
        0,
        Math.PI * 2
      )
      maskCtx.fill()
    }
    maskCtx.restore()
  }

  // Now use the maskCanvas to punch transparent holes in the white canvas
  // Where mask is white (painted areas), make transparent in output
  const maskImageData = maskCtx.getImageData(0, 0, originalWidth, originalHeight)
  const outputImageData = ctx.getImageData(0, 0, originalWidth, originalHeight)

  for (let i = 0; i < maskImageData.data.length; i += 4) {
    // If mask pixel is white (painted area), make output transparent
    if (maskImageData.data[i] > 128) {
      outputImageData.data[i + 3] = 0 // Set alpha to 0 (transparent = edit here)
    }
  }

  ctx.putImageData(outputImageData, 0, 0)

  // Convert to blob
  const dataUrl = tempCanvas.toDataURL("image/png")
  const binaryStr = atob(dataUrl.split(",")[1])
  const bytes = new Uint8Array(binaryStr.length)
  for (let i = 0; i < binaryStr.length; i++) {
    bytes[i] = binaryStr.charCodeAt(i)
  }
  return new Blob([bytes], { type: "image/png" })
}
